/**
 * Symmetric, non-reflexive adjacency graph.
 *
 * A node can have any number of adjacent nodes, and connections go both ways
 * between nodes (symmetric), but a node can't be adjacent to itself (non-reflexive).
 *
 * @example
 * const map = new ConfusableMap();
 * map.add('a', 'b');
 * map.add('a', 'c');
 *
 * map.get('a') // { 'b', 'c' }
 * map.get('b') // { 'a' }
 * map.get('c') // { 'a' }
 *
 * // edges are not transitive
 * map.get('b').has('c') // false
 */
class ConfusableMap {
  private readonly map = new Map<string, Set<string>>();
  private static readonly EMPTY: ReadonlySet<string> = new Set();

  add(a: string, b: string) {
    if (a === b) return; // no reflexivity
    (this.map.get(a) ?? this.map.set(a, new Set()).get(a)!).add(b);
    (this.map.get(b) ?? this.map.set(b, new Set()).get(b)!).add(a);
  }

  get(x: string): ReadonlySet<string> {
    return this.map.get(x) ?? ConfusableMap.EMPTY;
  }
}

const confusableMap = new ConfusableMap();

// voiceless vs voiced
confusableMap.add('p', 'b');
confusableMap.add('t', 'd');
confusableMap.add('k', 'g');
confusableMap.add('f', 'v');
confusableMap.add('s', 'z');
confusableMap.add('θ', 'ð');
confusableMap.add('tʃ', 'dʒ');

// nasals
confusableMap.add('n', 'm');
confusableMap.add('n', 'ŋ');

// fricatives
confusableMap.add('tʃ', 'ʃ');
confusableMap.add('f', 'θ');
confusableMap.add('v', 'ð');
confusableMap.add('s', 'ʃ');
confusableMap.add('s', 'θ');

// approximants / liquids
confusableMap.add('l', 'ɹ');
confusableMap.add('l', 'j');
confusableMap.add('l', 'w');
confusableMap.add('w', 'j');

// short vowels
confusableMap.add('æ', 'ɛ'); // "cat" vs "dress"
confusableMap.add('ɪ', 'ɛ'); // "kit" vs "dress"
confusableMap.add('ʌ', 'ɒ'); // "cup" vs "cop"
confusableMap.add('ʊ', 'uː'); // "book" vs "boot"
confusableMap.add('ʊ', 'ʌ'); // "book" vs "buck"

export default confusableMap;
